import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  CompanyUsageByModelItemDTO,
  CompanyUsageMemberRowDTO,
  CompanyUsageTopConversationItemDTO,
} from '@shared/schemas/usage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { getCompanyErrorMessage } from '@/features/company/company-errors';
import { formatDateTime, formatNumber } from '../lib/format';
import {
  getAdminCompanyTopConversations,
  getAdminCompanyUsageByModel,
  getAdminCompanyUsageMembers,
  getAdminCompanyUsageSummary,
  getAdminCompanyUsageTimeseries,
  getCompanyTopConversations,
  getCompanyUsageByModel,
  getCompanyUsageMembers,
  getCompanyUsageSummary,
  getCompanyUsageTimeseries,
} from '../services/usage.api';
import { CompanyUsageTimeseriesChart } from './CompanyUsageTimeseriesChart';
import { type UsageColumn, UsageDataTable } from './UsageDataTable';
import { UsagePeriodSelector } from './UsagePeriodSelector';
import { UsageTokenKpis } from './UsageTokenKpis';

function ErrorState({ error }: { error: unknown }) {
  return (
    <div className="rounded-[var(--radius-surface-2xl)] border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
      {getCompanyErrorMessage(error)}
    </div>
  );
}

interface CompanyUsageConsoleProps {
  /** Mode superadmin : cible cette entreprise via les routes miroir admin. */
  companyId?: string;
  title?: string;
}

export function CompanyUsageConsole({ companyId, title = 'Usage de l’entreprise' }: CompanyUsageConsoleProps) {
  const [days, setDays] = useState(30);
  const [membersPage, setMembersPage] = useState(1);
  const scopeKey = companyId ?? 'active';

  const summaryQuery = useQuery({
    queryFn: () => (companyId ? getAdminCompanyUsageSummary(companyId, days) : getCompanyUsageSummary(days)),
    queryKey: ['usage', 'company', scopeKey, 'summary', days],
  });
  const timeseriesQuery = useQuery({
    queryFn: () => (companyId ? getAdminCompanyUsageTimeseries(companyId, days) : getCompanyUsageTimeseries(days)),
    queryKey: ['usage', 'company', scopeKey, 'timeseries', days],
  });
  const modelsQuery = useQuery({
    queryFn: () => (companyId ? getAdminCompanyUsageByModel(companyId, days) : getCompanyUsageByModel(days)),
    queryKey: ['usage', 'company', scopeKey, 'models', days],
  });
  const conversationsQuery = useQuery({
    queryFn: () => (companyId ? getAdminCompanyTopConversations(companyId, days, 20) : getCompanyTopConversations(days, 20)),
    queryKey: ['usage', 'company', scopeKey, 'conversations', days],
  });
  const membersQuery = useQuery({
    queryFn: () => (companyId
      ? getAdminCompanyUsageMembers(companyId, days, membersPage, 20)
      : getCompanyUsageMembers(days, membersPage, 20)),
    queryKey: ['usage', 'company', scopeKey, 'members', days, membersPage],
  });

  const error = summaryQuery.error
    ?? timeseriesQuery.error
    ?? modelsQuery.error
    ?? conversationsQuery.error
    ?? membersQuery.error;

  const memberColumns: UsageColumn<CompanyUsageMemberRowDTO>[] = [
    {
      key: 'member',
      header: 'Membre',
      cell: (row) => (
        <span className="flex max-w-[20rem] flex-col">
          <span className="font-medium">{row.name}</span>
          <span className="truncate text-xs text-muted-foreground">{row.email}</span>
        </span>
      ),
    },
    { key: 'events', header: 'Événements', align: 'right', cell: (row) => formatNumber(row.eventsCount) },
    { key: 'tokens', header: 'Total tokens', align: 'right', cell: (row) => formatNumber(row.totals.totalTokens) },
    { key: 'last', header: 'Dernier usage', align: 'right', cell: (row) => formatDateTime(row.lastEventAt) },
  ];
  const conversationColumns: UsageColumn<CompanyUsageTopConversationItemDTO>[] = [
    {
      key: 'title',
      header: 'Conversation',
      cell: (row) => <span className="block max-w-[24rem] truncate font-medium">{row.title ?? row.conversationId}</span>,
    },
    { key: 'messages', header: 'Messages', align: 'right', cell: (row) => formatNumber(row.messageCount) },
    { key: 'tokens', header: 'Total tokens', align: 'right', cell: (row) => formatNumber(row.totals.totalTokens) },
    { key: 'last', header: 'Dernier usage', align: 'right', cell: (row) => formatDateTime(row.lastEventAt) },
  ];
  const modelColumns: UsageColumn<CompanyUsageByModelItemDTO>[] = [
    { key: 'model', header: 'Modèle', cell: (row) => <span className="font-medium">{row.model}</span> },
    { key: 'provider', header: 'Fournisseur', cell: (row) => row.provider },
    { key: 'events', header: 'Événements', align: 'right', cell: (row) => formatNumber(row.eventsCount) },
    { key: 'tokens', header: 'Total tokens', align: 'right', cell: (row) => formatNumber(row.totals.totalTokens) },
  ];

  return (
    <section className="w-full space-y-6" data-testid="company-usage-console">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        </div>
        <UsagePeriodSelector
          days={days}
          onChange={(nextDays) => {
            setDays(nextDays);
            setMembersPage(1);
          }}
        />
      </div>

      {error ? <ErrorState error={error} /> : null}

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="h-auto flex-wrap rounded-[var(--radius-surface-3xl)] bg-muted/50 p-1">
          <TabsTrigger value="overview">Vue d’ensemble</TabsTrigger>
          <TabsTrigger value="members">Membres</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="models">Modèles</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {summaryQuery.data ? (
            <UsageTokenKpis
              conversations={summaryQuery.data.conversationCount}
              events={summaryQuery.data.eventsCount}
              members={summaryQuery.data.memberCount}
              totals={summaryQuery.data.totals}
              webSearches={summaryQuery.data.webSearchCount}
            />
          ) : <Skeleton className="h-32 rounded-[var(--radius-surface-3xl)]" />}
          {timeseriesQuery.data
            ? <CompanyUsageTimeseriesChart data={timeseriesQuery.data} />
            : <Skeleton className="h-[380px] rounded-[var(--radius-surface-2xl)]" />}
        </TabsContent>

        <TabsContent value="members">
          <UsageDataTable
            columns={memberColumns}
            rows={membersQuery.data?.items ?? []}
            getRowKey={(row) => row.userId}
            isLoading={membersQuery.isLoading}
            emptyLabel="Aucun usage membre sur la période."
            title="Usage par membre"
            description={companyId
              ? 'Les résultats sont paginés et limités à cette entreprise.'
              : 'Les résultats sont paginés et limités à l’entreprise active.'}
            page={membersPage}
            pageCount={membersQuery.data?.meta.pageCount ?? 1}
            pageSize={membersQuery.data?.meta.pageSize}
            total={membersQuery.data?.meta.total}
            onPageChange={setMembersPage}
          />
        </TabsContent>

        <TabsContent value="conversations">
          <UsageDataTable
            columns={conversationColumns}
            rows={conversationsQuery.data?.items ?? []}
            getRowKey={(row) => row.conversationId}
            isLoading={conversationsQuery.isLoading}
            emptyLabel="Aucune conversation sur la période."
            title="Conversations les plus consommatrices"
            page={1}
            pageCount={1}
            onPageChange={() => undefined}
          />
        </TabsContent>

        <TabsContent value="models">
          <UsageDataTable
            columns={modelColumns}
            rows={modelsQuery.data?.items ?? []}
            getRowKey={(row) => `${row.provider}:${row.model}`}
            isLoading={modelsQuery.isLoading}
            emptyLabel="Aucun modèle utilisé sur la période."
            title="Usage par modèle"
            page={1}
            pageCount={1}
            onPageChange={() => undefined}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}
